import {useQueries} from '@tanstack/react-query';
import {DEFAULT_COMBINE_GRAPHS_MODE, ActionsMenu,
  hasBlueprintPermissions, interpolateRoute, notifier,
  request} from 'apstra-ui-common';
import {filter, find, get, head, includes, isEmpty, isNumber, keys, map, some, transform} from 'lodash';
import {observer} from 'mobx-react';
import moment from 'moment';
import {FC, Fragment, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {Button, Grid, Header, Label, Message, Popup} from 'semantic-ui-react';

import {apiQueryOptions} from '../../apiQueryOptions';
import {
  DEFAULT_STAGE_DATA_SOURCE,
  DEFAULT_STAGE_TIME_SERIES_AGGREGATION,
  DEFAULT_STAGE_TIME_SERIES_DURATION,
  STAGE_DATA_SOURCE,
  STAGE_DYNAMIC_POPUP_MESSAGE,
  WIDGET_TYPE_STAGE,
} from '../consts';
import {filtersToQueryParam, sortingToQueryParam} from '../../queryParamUtils';
import {
  getStageSorting, processorCanRaiseAnomalies,
  getStageRenderingStrategy, processorCanRaiseWarnings, getDefaultAggregationType,
} from '../stageUtils';
import {useLastUsedStageOptions} from '../hooks/useLastUsedStageOptions';
import {useSearchStringFilters} from '../../useSearchStringFilters';
import {descriptionRenderer} from '../descriptionRenderer';
import {StageData} from './StageData';
import {getStageSearchSchema} from './StageSearchBox';
import checkForPatterns, {ProbePattern} from '../checkForPatterns';
import DashboardChoiceModal from './DashboardChoiceModal';
import IBAContext from '../IBAContext';
import {ProbeStageDiskUsageLabel} from './ProbeStageDiskUsageLabel';
import {Probe, ProbeProcessor, ProbeStage as ProbeStageType, ProcessorDefinition, StageDataSource} from '../types';
import userStore from '../../userStore';
import {StageEditor} from './StageEditor';
import {ProbeError} from './ProbeDetails/types';

import {ReactComponent as StageDynamic} from '../../../styles/icons/iba/stage-dynamic.svg';

import './ProbeStage.less';

export const buildStageFetchPayload = ({
  isFreeform, processorDefinitions, probe, processor, stage,
  activePage, pageSize, filters, sorting, patternDescription, usePattern,
}: {
  isFreeform: boolean;
  processorDefinitions: ProcessorDefinition[];
  probe: Probe;
  processor: ProbeProcessor;
  stage: ProbeStageType;
  activePage: number;
  pageSize: number;
  filters: any;
  sorting: ProbeStageProps['sorting'];
  patternDescription: ProbePattern | null;
  usePattern: boolean;
}) => {
  const stageFilter = filtersToQueryParam(
    (usePattern && !!patternDescription?.renderingStrategy.filterTransformer) ?
    patternDescription.renderingStrategy.filterTransformer(filters.filter) : filters.filter,
    getStageSearchSchema({
      processorDefinitions, probe, processor, stage, dataSource: filters.dataSource, isFreeform
    })
  );

  const body: any = {stage: stage.name};
  const sortingAsString = sortingToQueryParam(
    getStageSorting({sortingOverrides: sorting ?? {}, propertyNames: keys(stage.keys)})
  );

  if (sortingAsString.length) body.order_by = sortingAsString;

  if (stageFilter) body.filter = stageFilter;
  if (usePattern && !isEmpty(patternDescription?.fetchDataForRelatedStages)) {
    body.include_stage = map(filter(
      patternDescription?.fetchDataForRelatedStages, ({stageName}) => stageName !== stage.name
    ), 'stageName');
  }
  if (filters.anomalousOnly) body.anomalous_only = true;

  const {timeSeriesDuration} = filters;

  if (patternDescription?.shouldFetchPersistedStageData && usePattern ||
    (patternDescription?.shouldFetchRawPersistedStageData && usePattern) ||
    filters.dataSource === STAGE_DATA_SOURCE.time_series) {
    if (isNumber(timeSeriesDuration)) {
      body.timeSeriesDuration = timeSeriesDuration;
    } else {
      body.begin_time = timeSeriesDuration?.start;
      body.end_time = timeSeriesDuration?.end;
    }

    if (filters.dataSource === STAGE_DATA_SOURCE.time_series && filters.timeSeriesAggregation) {
      // default behavior for time series
      const stageAggregation = {
        period: filters.timeSeriesAggregation,
        metrics: filters.aggregationType === 'unset' ? {} : {[filters.valueColumnName]: filters.aggregationType},
      };
      body.aggregation = {
        [stage.name]: stageAggregation
      };
    }

    if (usePattern && patternDescription?.shouldFetchPersistedStageData) {
      const aggregation = transform(
        patternDescription?.fetchDataForRelatedStages,
        (acc, {doNotUseAggregation, hasPersistedData, stageName, values}) => {
          if (!hasPersistedData || doNotUseAggregation) return;
          acc[stageName] = {
            period: filters.timeSeriesAggregation,
            metrics: !values || isEmpty(values) || filters.aggregationType === 'unset' ? {} :
              transform(values, (acc, value) => {acc[value] = filters.aggregationType;}, {})
          };
          return acc;
        },
        {}
      );
      if (!isEmpty(aggregation)) {
        body.aggregation = aggregation;
      }
    }
  }

  if (processorCanRaiseAnomalies(processor)) {
    body.anomaly_context = true;
  }

  const queryParams: any = new URLSearchParams();
  if (activePage && pageSize && !(usePattern && patternDescription?.renderingStrategy.noPagination)) {
    queryParams.set('page', activePage);
    queryParams.set('per_page', pageSize);
  }
  if (filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings) {
    if (body.order_by) queryParams.set('orderby', body.order_by);
    if (body.filter) queryParams.set('filter', body.filter);
  }

  return {body, queryParams: queryParams.toString()};
};

const anomalyRemediationPossible = ({stage, blueprintPermissions}) => (
  hasBlueprintPermissions({blueprintPermissions, action: 'edit'}) &&
  includes(['Fabric missing VLAN configs anomaly', 'Hypervisor missing VLAN configs anomaly'], stage.name) &&
  !!stage.anomaly_count
);

const updateTimeRangeIfNeeded = (body) => {
  const {timeSeriesDuration, ...restBody} = body;
  if (timeSeriesDuration) {
    const now = moment();
    return {
      ...restBody,
      begin_time: now.clone().add(-timeSeriesDuration, 's').toISOString(),
      end_time: null,
    };
  }
  return body;
};

const USER_STORE_KEY = 'probeStage';

type ProbeStageProps = {
  actionInProgress?: boolean;
  anomalousOnly?: boolean;
  combineGraphs?: 'none' | 'linear' | 'stacked';
  compact?: boolean;
  dataSource?: StageDataSource;
  noLastOptionsRestore?: boolean;
  activePage?: number;
  pageSize?: number;
  sorting?: Record<string, 'asc' | 'desc'>;
  editable?: boolean;
  errors?: ProbeError[];
  filter?: any;
  probe: Probe;
  processor: ProbeProcessor;
  showContextInfo?: boolean;
  spotlightMode?: boolean;
  stage: ProbeStageType;
  loaderContent?: ReactNode;
  stageLink?: ReactNode;
  timeSeriesAggregation?: number;
  timeSeriesDuration?: number | {start: string, end: string};
  aggregationType?: string;
  valueColumnName?: string;
  visibleColumns?: string[];
} & ({
  editable: true;
  processorDefinition: ProcessorDefinition;
} | {
  editable?: false;
})

const ProbeStage: FC<ProbeStageProps> = observer((props) => {
  const [dashboardChoiceModalProps, setDashboardChoiceModalProps] = useState<any>(null);

  const {blueprintId, isFreeform, processorDefinitions, routes, blueprintPermissions} = useContext(IBAContext);
  const telemetryServiceWarnings = get(props.stage, ['warnings'], 0);
  const defaultDataSource = processorCanRaiseWarnings(props.processor) && telemetryServiceWarnings > 0 ?
      STAGE_DATA_SOURCE.telemetry_service_warnings : DEFAULT_STAGE_DATA_SOURCE;
  const {
    editable, compact,
    probe, processor, stage, loaderContent,
    actionInProgress, errors,
    visibleColumns,
    filter,
    anomalousOnly = false,
    spotlightMode = false,
    dataSource = defaultDataSource,
    showContextInfo: showContextInfoFromProps,
    stageLink,
    timeSeriesDuration = DEFAULT_STAGE_TIME_SERIES_DURATION,
    aggregationType,
    timeSeriesAggregation = DEFAULT_STAGE_TIME_SERIES_AGGREGATION,
    combineGraphs = DEFAULT_COMBINE_GRAPHS_MODE,
    valueColumnName = head(keys(stage.values))!,
    activePage: activePageFromProps,
    pageSize: pageSizeFromProps,
    sorting: sortingFromProps,
    noLastOptionsRestore,
  } = props;
  const isProbeOperational = probe?.state === 'operational';
  const defaultPageSize = pageSizeFromProps ?? userStore.getStoreValue([USER_STORE_KEY, 'pageSize']);
  const setUserStoreProps = (value) => {
    if (value?.pageSize > 1) {
      userStore.setStoreValueFn(USER_STORE_KEY)(value);
    }
  };

  const {
    activePage, pageSize, updatePagination, filters: currentFilters, updateFilters, sorting, updateSorting
  } = useSearchStringFilters<{
    filter: any;
    dataSource: StageDataSource;
    anomalousOnly: boolean;
    spotlightMode: boolean
    timeSeriesDuration: number | {start: string, end: string};
    timeSeriesAggregation: number;
    valueColumnName: string;
    combineGraphs: 'none' | 'linear' | 'stacked';
    showContextInfo?: boolean;
    aggregationType?: string;
  }>({
    stateQueryParam: compact ? null : 'stage-filter',
    defaultFilters: {
      filter, dataSource, anomalousOnly, spotlightMode,
      timeSeriesDuration, timeSeriesAggregation, valueColumnName, combineGraphs,
      aggregationType,
    },
    defaultPageSize: spotlightMode ? 1 : defaultPageSize,
    setUserStoreProps: setUserStoreProps,
    defaultActivePage: activePageFromProps,
    defaultSorting: sortingFromProps,
  });

  const patternDescription = useMemo(
    () => checkForPatterns({probe, stageName: stage.name, dataSource: currentFilters.dataSource}),
    [currentFilters.dataSource, probe, stage.name]
  );
  const defaultShowContextInfo = showContextInfoFromProps ??
      (!!patternDescription && !patternDescription.renderingStrategy?.hiddenContextByDefault);
  const defaultAggregationType = getDefaultAggregationType(stage, valueColumnName, patternDescription);
  const defaultFilter = patternDescription?.renderingStrategy.defaultFilter ?? null;

  const filters = useMemo(() => ({
    ...currentFilters,
    showContextInfo: currentFilters.showContextInfo ?? defaultShowContextInfo,
    aggregationType: currentFilters.aggregationType ?? defaultAggregationType,
    filter: currentFilters.filter ?? defaultFilter,
  }), [currentFilters, defaultAggregationType, defaultFilter, defaultShowContextInfo]);

  const showContext: boolean = filters.showContextInfo || !!patternDescription?.renderingStrategy?.alwaysUseContext;
  const renderingStrategy = getStageRenderingStrategy({
    stage, probe,
    dataSource: filters.dataSource,
    showContext: !!showContext,
  });
  const isTelemetryServiceWarningsDataSource =
      filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings;
  const usePattern: boolean = !!patternDescription &&
    (filters.showContextInfo || !!patternDescription.renderingStrategy?.alwaysUseContext);

  const onUserUpdateFilters = useLastUsedStageOptions({
    enabled: !noLastOptionsRestore,
    filters: currentFilters, probe, processor, stage, updateFilters
  });

  const {queryParams, body} = useMemo(
    () => buildStageFetchPayload({
      isFreeform, processorDefinitions, probe, processor, stage,
      activePage, pageSize, filters, sorting: sorting as Record<string, 'asc' | 'desc'>, patternDescription, usePattern
    }),
    [
      isFreeform, processorDefinitions, probe, processor, stage, activePage,
      pageSize, filters, sorting, patternDescription, usePattern
    ]);

  const shouldNotFetch: boolean = editable || probe.disabled || probe.state === 'maintenance' ||
    (filters.showContextInfo || !!patternDescription?.renderingStrategy?.alwaysUseContext) &&
    patternDescription?.renderingStrategy?.shouldFetchData?.({filters}) === false;

  const {stageItems = [], totalCount = null, patternData, loaderVisible, fetchDataError} = useQueries({
    queries: [{
      ...apiQueryOptions.telemetryServiceWarnings(blueprintId, probe.id, stage.name, queryParams),
      queryFn: ({signal}) => request(
        interpolateRoute(routes.telemetryServiceWarnings, {blueprintId, probeId: probe.id, stageName: stage.name}),
        {signal, queryParams, method: 'GET'}
      ),
      enabled: !shouldNotFetch && filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings,
    }, {
      ...apiQueryOptions.probeStage(blueprintId, probe.id, queryParams, body),
      queryFn: ({signal}) => request(
        interpolateRoute(routes.probeStage, {blueprintId, probeId: probe.id}),
        {signal, queryParams, method: 'POST', body: JSON.stringify(updateTimeRangeIfNeeded(body))}
      ),
      enabled: !shouldNotFetch && filters.dataSource !== STAGE_DATA_SOURCE.telemetry_service_warnings,
    }, {
      queryFn: ({signal}) => patternDescription?.fetchData?.({
        blueprintId, probe, processor, stage, routes, signal, filters
      }) ?? Promise.resolve(null),
      queryKey: patternDescription?.fetchDataKey?.({
        blueprintId, probe, processor, stage, routes, filters
      }) ?? [],
      enabled: !shouldNotFetch && showContext && !!patternDescription?.fetchData,
      refetchInterval: 10000,
    }],
    combine: (results) => {
      const {data} = filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings ?
        results[0] : results[1];
      return {
        stageItems: data?.items,
        totalCount: data?.total_count,
        patternData: results[2].data,
        loaderVisible: some(results, (r) => r.isLoading),
        fetchDataError: find(results, (r) => !!r.error)?.error,
      };
    }
  });

  return (
    <Grid className='probe-stage'>
      {!compact &&
        <>
          <Grid.Row>
            <Grid.Column width={13}>
              <Header className='probe-contents-header'>
                {!editable && map([
                  stage.dynamic &&
                    <Popup
                      trigger={
                        <Label
                          content='Dynamic'
                          icon={<StageDynamic className='stage-icon' />}
                        />
                      }
                      content={STAGE_DYNAMIC_POPUP_MESSAGE}
                      wide
                    />,
                  stage.enable_metric_logging &&
                    <ProbeStageDiskUsageLabel probe={probe} stage={stage} />,
                  patternDescription?.shouldFetchPersistedStageData &&
                  patternDescription?.shouldFetchRawPersistedStageData &&
                  showContext &&
                    <Label content='Aggregation affects only sample history' />,
                ].filter(Boolean), (label, index) => (
                  <Fragment key={index}>
                    {!!index && <>&nbsp;</>}
                    {label}
                  </Fragment>
                ))}
              </Header>
            </Grid.Column>
            {!editable && isProbeOperational && !isTelemetryServiceWarningsDataSource &&
              <Grid.Column width={3} textAlign='right' className='create-button'>
                <ActionsMenu
                  size='small'
                  items={[{
                    icon: 'apstra-icon apstra-icon-widget',
                    title: 'Create dashboard widget',
                    hasPermissions:
                        hasBlueprintPermissions({blueprintPermissions, action: 'edit'}),
                    notPermittedMessage: 'You do not have permission to create',
                    onClick: () => setDashboardChoiceModalProps({
                      open: true,
                      widget: {
                        type: WIDGET_TYPE_STAGE,
                        label: stage.name,
                        probe_id: probe.id,
                        stage_name: stage.name,
                        data_source: filters.dataSource,
                        filter:
                            (renderingStrategy?.filterSerializer || filtersToQueryParam)(
                              filters.filter,
                              getStageSearchSchema({
                                processorDefinitions, probe, processor, stage, stageItems,
                                dataSource: filters.dataSource, isFreeform
                              })
                            ),
                        anomalous_only: filters.anomalousOnly,
                        show_context: filters.showContextInfo,
                        spotlight_mode: filters.spotlightMode,
                        orderby: sortingToQueryParam(sorting),
                        aggregation_type: filters.aggregationType,
                        aggregation_period: filters.timeSeriesAggregation,
                        ...(isNumber(filters.timeSeriesDuration) &&
                            {time_series_duration: filters.timeSeriesDuration}
                        ),
                        value_column_name: filters.valueColumnName,
                        combine_graphs: filters.combineGraphs,
                      }
                    }),
                  }]}
                />
                {dashboardChoiceModalProps?.open &&
                  <DashboardChoiceModal
                    open={false}
                    onClose={() => setDashboardChoiceModalProps(null)}
                    {...dashboardChoiceModalProps}
                  />
                }
              </Grid.Column>
            }
          </Grid.Row>
          {!editable && stage.description && descriptionRenderer.Value &&
            <Grid.Row>
              <Grid.Column width={16}>
                <descriptionRenderer.Value value={stage.description} />
              </Grid.Column>
            </Grid.Row>
          }
        </>
      }
      {!editable && anomalyRemediationPossible({
        stage, blueprintPermissions
      }) &&
        <Grid.Row>
          <Grid.Column width={16}>
            <VSphereAnomalyRemediator
              probe={probe}
              stage={stage}
            />
          </Grid.Column>
        </Grid.Row>
      }
      <Grid.Row>
        <Grid.Column width={16}>
          {editable ?
            <StageEditor
              stage={stage}
              processor={processor}
              actionInProgress={actionInProgress}
              errors={errors}
              processorDefinition={props.processorDefinition}
            />
            : probe.disabled ?
              <Message
                info
                icon='info circle'
                content='This probe is disabled.'
              />
            : probe.state === 'configuring' ?
              <Message
                info
                icon='hourglass half'
                content='This probe is configuring.'
              />
            : probe.state === 'maintenance' ?
              <Message
                info
                icon='wrench'
                content='This probe is in maintenance mode.'
              />
            : probe.state === 'error' &&
              // checking the probe state to determine if we need to show telemetry warnings
              probe.probe_state?.processors_configuration?.state !== 'operational' &&
              probe.probe_state?.pipeline?.state !== 'operational'
            ?
              <Message
                error
                icon='warning sign'
                content='This probe is in error state.'
              />
            :
              <StageData
                stage={stage}
                stageItems={stageItems}
                visibleColumns={visibleColumns}
                probe={probe}
                processor={processor}
                compact={compact}
                totalCount={totalCount}
                activePage={activePage}
                pageSize={pageSize}
                filters={filters}
                sorting={sorting}
                updatePagination={updatePagination}
                updateFilters={onUserUpdateFilters}
                updateSorting={updateSorting}
                patternData={patternData}
                stageLink={stageLink}
                loaderVisible={loaderVisible}
                loaderContent={loaderContent}
                fetchDataError={fetchDataError}
                renderingStrategy={renderingStrategy}
                patternDescription={patternDescription}
              />
          }
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
});

ProbeStage.displayName = 'ProbeStage';

type VSphereAnomalyRemediatorProps = {
  probe: Probe;
  stage: ProbeStageType;
}

const VSphereAnomalyRemediator: FC<VSphereAnomalyRemediatorProps> = observer(({
  probe,
  stage,
}) => {
  const {blueprintId, routes, anomalyRemediationUrl} = useContext(IBAContext);
  const [actionInProgress, setActionInProgress] = useState(false);

  const resolveAnomalies = useCallback(async () => {
    let error: any = null;
    setActionInProgress(true);
    try {
      await request(
        interpolateRoute(routes.vShpereAnomalyResolver, {blueprintId}),
        {method: 'POST', body: JSON.stringify({probe_id: probe.id, stage_name: stage.name})}
      );
    } catch (e) {
      error = e;
    }
    if (!error) {
      notifier.notify({message: 'Anomalies were remediated successfully'});
      window.location.href = `/#${anomalyRemediationUrl}`;
    } else {
      notifier.showError(error);
      setActionInProgress(false);
    }
  }, [anomalyRemediationUrl, blueprintId, probe.id, routes.vShpereAnomalyResolver, stage.name]);

  return (
    <Message info>
      <Message.Content>
        <Message.Header>{'Anomaly Remediation'}</Message.Header>
        <p>{'It is possible to automatically fix the anomalies.'}</p>
        <Button
          primary
          icon='wrench'
          labelPosition='left'
          content='Remediate Anomalies'
          disabled={actionInProgress}
          onClick={resolveAnomalies}
        />
      </Message.Content>
    </Message>
  );
});

export default ProbeStage;
